import React, { useState } from "react";
import { Box, Button, Text, Layer, RadioButtonGroup } from "grommet";
import { View } from "grommet-icons";
import { CustomTabs } from "@features/shared/CustomTabs";
import TagTextArea from "@features/shared/TagTextArea";
import { normalizeTagSpacing } from "@features/shared/normalizeTagSpacing";
import { useTranslation } from "react-i18next";

// Maximum length (in characters) for any SMS body in the check-in flow editors.
// Twilio segments long SMS, but we cap here to keep messages readable and to
// avoid surprise multi-segment costs.
export const SMS_MAX_LENGTH = 200;

export default function LocalizedTextEditor({
  value = { en: "", fr: "" },
  onChange,
  defaultValue,
  useCustom,
  onUseCustomChange,
  hideUseCustomToggle,
  maxLength = SMS_MAX_LENGTH,
  hint,
  showCheckinLink,
  showChatLink,
  tags,
}) {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (lang, val) => {
    onChange({ ...value, [lang]: val });
  };

  const handleBlur = (lang, freshText) => {
    const current = freshText !== undefined ? freshText : value?.[lang];
    const normalized = normalizeTagSpacing(current, lang);
    if (normalized !== current) {
      onChange({ ...value, [lang]: normalized });
    }
  };

  const previewSource = useCustom ? value : defaultValue;

  const replaceTags = (text) => {
    if (!text) return text;
    const mockTags = {
      firstName: "John",
      lastName: "Doe",
      hotel: "Hotel Name",
      checkinDate: "Mon, Mar 16, 2026",
      checkoutDate: "Tue, Mar 17, 2026",
      checkinTime: "3:00 PM",
      checkoutTime: "12:00 PM",
      roomsAndCodes: "Room 101: 1234#",
      door: "101",
      code: "1234#",
      commonAreaPasscode: "5678#",
      magicLink: "https://loxekey.app.link/go",
      mobileAppLink: "https://loxekey.app.link/go",
      pronounsFr: { le: "le ", au: "au ", du: "du " },
    };
    let result = text;
    for (const [tag, val] of Object.entries(mockTags)) {
      if (typeof val === "object") {
        for (const [subkey, subval] of Object.entries(val)) {
          result = result.replace(
            new RegExp(`\\{${tag}\\.${subkey}\\}`, "g"),
            subval,
          );
        }
      } else {
        result = result.replace(new RegExp(`\\{${tag}\\}`, "g"), val);
      }
    }
    return result;
  };

  const enLen = (value?.en || "").length;
  const frLen = (value?.fr || "").length;
  const enOverLimit = enLen > maxLength;
  const frOverLimit = frLen > maxLength;

  const tabs = [
    {
      title: t("common.english"),
      key: "en",
      content: (
        <Box gap="xsmall">
          <TagTextArea
            value={value?.en || ""}
            onChange={(val) => handleChange("en", val)}
            onBlur={(freshText) => handleBlur("en", freshText)}
            maxLength={maxLength}
            tags={tags}
          />
          <Box direction="row" justify="end" gap="small" align="center">
            {enOverLimit && (
              <Text size="xsmall" color="status-critical">
                {t("localizedText.messageTooLong")}
              </Text>
            )}
            <Text
              size="xsmall"
              color={enOverLimit ? "status-critical" : "dark-4"}
            >
              {enLen}/{maxLength}
            </Text>
          </Box>
        </Box>
      ),
    },
    {
      title: t("common.french"),
      key: "fr",
      content: (
        <Box gap="xsmall">
          <TagTextArea
            value={value?.fr || ""}
            onChange={(val) => handleChange("fr", val)}
            onBlur={(freshText) => handleBlur("fr", freshText)}
            maxLength={maxLength}
            tags={tags}
          />
          <Box direction="row" justify="end" gap="small" align="center">
            {frOverLimit && (
              <Text size="xsmall" color="status-critical">
                {t("localizedText.messageTooLong")}
              </Text>
            )}
            <Text
              size="xsmall"
              color={frOverLimit ? "status-critical" : "dark-4"}
            >
              {frLen}/{maxLength}
            </Text>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box gap="small">
      <Box direction="row" gap="medium" align="center">
        {!hideUseCustomToggle && (
          <RadioButtonGroup
            name="textMode"
            direction="row"
            gap="medium"
            options={[
              { label: t("localizedText.useDefault"), value: "default" },
              { label: t("localizedText.custom"), value: "custom" },
            ]}
            value={useCustom ? "custom" : "default"}
            onChange={(e) => onUseCustomChange(e.target.value === "custom")}
          />
        )}
        <Button
          icon={<View size="small" />}
          label={t("localizedText.preview")}
          size="small"
          onClick={() => setShowPreview(true)}
        />
        {useCustom && defaultValue && (
          <Button
            plain
            label={
              <Text size="small" color="brand">
                {t("localizedText.revertToDefault")}
              </Text>
            }
            onClick={() => {
              if (window.confirm(t("localizedText.revertToDefaultConfirm"))) {
                onChange(defaultValue);
              }
            }}
          />
        )}
      </Box>

      {useCustom && (
        <>
          {hint && (
            <Text
              size="small"
              color="black"
              style={{ fontStyle: "italic", marginBottom: "8px" }}
            >
              {hint}
            </Text>
          )}
          <CustomTabs
            tabs={tabs}
            activeIndex={activeLang}
            onTabChange={setActiveLang}
          />
        </>
      )}

      {showPreview && (
        <Layer
          onEsc={() => setShowPreview(false)}
          onClickOutside={() => setShowPreview(false)}
          responsive
          margin="medium"
        >
          <Box pad="medium" width={{ min: "320px", max: "400px" }} gap="medium">
            <Box direction="row" justify="between" align="center">
              <Text weight="bold">{t("localizedText.smsPreview")}</Text>
              <Button
                label={t("localizedText.close")}
                size="small"
                onClick={() => setShowPreview(false)}
              />
            </Box>
            <CustomTabs
              tabs={[
                {
                  title: t("common.english"),
                  key: "en",
                  content: (
                    <Box
                      background="#e5e5ea"
                      round="medium"
                      pad="medium"
                      style={{ minHeight: 120 }}
                    >
                      <Box
                        background="white"
                        round="12px"
                        pad="small"
                        style={{
                          maxWidth: "85%",
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <Text size="small">
                          {replaceTags(previewSource?.en) ||
                            t("localizedText.empty")}
                          {showCheckinLink && (
                            <>
                              {"\n"}
                              <Text
                                size="small"
                                color="brand"
                                style={{ textDecoration: "underline" }}
                              >
                                https://loxekey.app.link/go
                              </Text>
                            </>
                          )}
                          {showChatLink && (
                            <>
                              {"\n"}
                              <Text
                                size="small"
                                color="brand"
                                style={{ textDecoration: "underline" }}
                              >
                                https://loxekey.app.link/chat
                              </Text>
                            </>
                          )}
                        </Text>
                      </Box>
                    </Box>
                  ),
                },
                {
                  title: t("common.french"),
                  key: "fr",
                  content: (
                    <Box
                      background="#e5e5ea"
                      round="medium"
                      pad="medium"
                      style={{ minHeight: 120 }}
                    >
                      <Box
                        background="white"
                        round="12px"
                        pad="small"
                        style={{
                          maxWidth: "85%",
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <Text size="small">
                          {replaceTags(previewSource?.fr) ||
                            t("localizedText.empty")}
                          {showCheckinLink && (
                            <>
                              {"\n"}
                              <Text
                                size="small"
                                color="brand"
                                style={{ textDecoration: "underline" }}
                              >
                                https://loxekey.app.link/go
                              </Text>
                            </>
                          )}
                          {showChatLink && (
                            <>
                              {"\n"}
                              <Text
                                size="small"
                                color="brand"
                                style={{ textDecoration: "underline" }}
                              >
                                https://loxekey.app.link/chat
                              </Text>
                            </>
                          )}
                        </Text>
                      </Box>
                    </Box>
                  ),
                },
              ]}
              activeIndex={activeLang}
              onTabChange={setActiveLang}
            />
          </Box>
        </Layer>
      )}
    </Box>
  );
}
